import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  BookOpen, QrCode, Calculator, HelpCircle, FlaskConical,
  Bell, Brain, ChevronRight, Loader2, CheckCircle2, AlertCircle,
  Globe
} from "lucide-react";

const CONTENT = {
  en: {
    heroTitle: "Your Free Lifetime Digital Access",
    heroSub: "The purchase of this hardcover edition includes free, lifetime access to the CHARSTAN Reader Hub.",
    codeLabel: "Enter Your Access Code",
    codePlaceholder: "BDIN-XXXX-XXXX",
    codeHelp: "Your unique access code is printed on the inside back cover of your book.",
    validateBtn: "Validate Code",
    activateTitle: "Activate Your Access",
    emailLabel: "Email Address",
    emailPlaceholder: "you@example.com",
    nameLabel: "Your Name",
    namePlaceholder: "Your full name",
    activateBtn: "Activate Free Access",
    features: [
      { icon: BookOpen, title: "Full Digital Edition", desc: "Complete text — all volumes, all chapters" },
      { icon: Calculator, title: "Live Formula Calculators", desc: "11 interactive calculators with real-time results" },
      { icon: HelpCircle, title: "Extended Q&A", desc: "200+ questions with audit drill mode" },
      { icon: FlaskConical, title: "Interactive Labs", desc: "10 lab simulations with auto-grading" },
      { icon: Bell, title: "Regulatory Updates", desc: "QMSR and standards change alerts" },
      { icon: Brain, title: "Auditor's Brain", desc: "AI companion for audit preparation" },
    ],
    scanTitle: "Scan to Access",
    scanUrl: "charstan.com/readers",
    calcTitle: "Calculators",
    calcUrl: "charstan.com/calculators",
    alreadyHave: "Already activated?",
    loginLink: "Sign in to your Reader Dashboard",
    langSwitch: "Versão em Português",
    langSwitchUrl: "/leitores",
  },
  pt: {
    heroTitle: "Seu Acesso Digital Gratuito",
    heroSub: "A compra desta edição capa dura inclui acesso gratuito e vitalício ao Hub do Leitor CHARSTAN.",
    codeLabel: "Insira Seu Código de Acesso",
    codePlaceholder: "BDIN-XXXX-XXXX",
    codeHelp: "Seu código de acesso único está impresso na contracapa interna deste livro.",
    validateBtn: "Validar Código",
    activateTitle: "Ativar Seu Acesso",
    emailLabel: "Endereço de E-mail",
    emailPlaceholder: "você@exemplo.com",
    nameLabel: "Seu Nome",
    namePlaceholder: "Seu nome completo",
    activateBtn: "Ativar Acesso Gratuito",
    features: [
      { icon: BookOpen, title: "Edição Digital Completa", desc: "Texto completo — todos os volumes, todos os capítulos" },
      { icon: Calculator, title: "Calculadoras ao Vivo", desc: "11 calculadoras interativas com resultados em tempo real" },
      { icon: HelpCircle, title: "P&R Estendida", desc: "Mais de 200 perguntas com modo drill de auditoria" },
      { icon: FlaskConical, title: "Labs Interativos", desc: "10 simulações laboratoriais com autoavaliação" },
      { icon: Bell, title: "Atualizações Regulatórias", desc: "Alertas de mudanças no QMSR e normas" },
      { icon: Brain, title: "Cérebro do Auditor", desc: "Companheiro IA para preparação de auditoria" },
    ],
    scanTitle: "Escanear para Acessar",
    scanUrl: "charstan.com/leitores",
    calcTitle: "Calculadoras",
    calcUrl: "charstan.com/calculadoras",
    alreadyHave: "Já ativou?",
    loginLink: "Acesse seu Painel do Leitor",
    langSwitch: "English Version",
    langSwitchUrl: "/readers",
  },
};

const ReaderHubLandingPage = () => {
  const { t } = useTranslation('platform');
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const locale = location.pathname === "/leitores" ? "pt" : "en";
  const content = CONTENT[locale];

  const [code, setCode] = useState("");
  const [codeValid, setCodeValid] = useState(null);
  const [codeBookId, setCodeBookId] = useState(null);
  const [validationMsg, setValidationMsg] = useState("");
  const [validating, setValidating] = useState(false);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState("");
  const [showActivation, setShowActivation] = useState(false);

  const formatCode = (val) => {
    const clean = val.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const parts = [];
    if (clean.length > 0) parts.push(clean.slice(0, 4));
    if (clean.length > 4) parts.push(clean.slice(4, 8));
    if (clean.length > 8) parts.push(clean.slice(8, 12));
    return parts.join("-");
  };

  const handleValidate = async () => {
    if (!code.trim()) return;
    setValidating(true);
    setValidationMsg("");
    setCodeValid(null);
    try {
      const res = await axios.post(`${API}/reader-hub/validate-code`, { code: code.trim() });
      setCodeValid(res.data.valid);
      setValidationMsg(res.data.message);
      if (res.data.valid) {
        setCodeBookId(res.data.book_id);
        setShowActivation(true);
      }
    } catch (err) {
      setCodeValid(false);
      setValidationMsg(err.response?.data?.detail || "Validation failed. Try again.");
    } finally {
      setValidating(false);
    }
  };

  const handleActivate = async () => {
    if (!email.trim()) return;
    setActivating(true);
    setActivationError("");
    try {
      const res = await axios.post(`${API}/reader-hub/activate`, {
        code: code.trim(),
        email: email.trim(),
        name: name.trim(),
      }, { withCredentials: true });
      if (res.data.success) {
        // Set user in auth context so ProtectedRoute allows access
        setUser({
          user_id: res.data.user_id,
          email: res.data.email,
          name: res.data.name,
          role: res.data.role,
          reader_books: res.data.reader_books,
        });
        navigate("/reader-dashboard", { state: { justActivated: true, bookId: res.data.book_id } });
      }
    } catch (err) {
      setActivationError(err.response?.data?.detail || "Activation failed. Try again.");
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="reader-hub-landing">
      {/* Nav bar */}
      <nav className="border-b border-[#1E3A5F]/50 px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#1A6BCC] rounded flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-sm tracking-wide">CHARSTAN</span>
          <span className="text-slate-500 text-xs">|</span>
          <span className="text-slate-400 text-xs">Brain Drain Series</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(t.langSwitchUrl)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
            data-testid="lang-switch"
          >
            <Globe className="w-3.5 h-3.5" />
            {content.langSwitch}
          </button>
          <button
            onClick={() => navigate("/login")}
            className="text-xs text-[#00C8E0] hover:text-white transition-colors"
            data-testid="login-link"
          >
            {content.alreadyHave} {content.loginLink}
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#132340] border border-[#1E3A5F] rounded-full px-4 py-1.5 mb-6">
            <QrCode className="w-4 h-4 text-[#00C8E0]" />
            <span className="text-xs text-slate-300 font-medium tracking-wide uppercase">Brain Drain Series</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight" data-testid="hero-title">
            {content.heroTitle}
          </h1>
          <p className="text-slate-400 text-base max-w-2xl mx-auto leading-relaxed">
            {content.heroSub}
          </p>
        </div>

        {/* Code Entry */}
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-8 mb-10 max-w-xl mx-auto" data-testid="code-entry-section">
          <label className="block text-sm font-bold text-slate-300 mb-2">{content.codeLabel}</label>
          <div className="flex gap-3 mb-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(formatCode(e.target.value))}
              placeholder={content.codePlaceholder}
              maxLength={14}
              className="flex-1 bg-[#0A1628] border border-[#1E3A5F] rounded px-4 py-3 text-white text-lg font-mono tracking-widest placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none transition-colors"
              data-testid="code-input"
            />
            <button
              onClick={handleValidate}
              disabled={validating || code.length < 14}
              className="bg-[#1A6BCC] hover:bg-[#1A6BCC]/80 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded font-bold text-sm transition-colors flex items-center gap-2"
              data-testid="validate-btn"
            >
              {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {content.validateBtn}
            </button>
          </div>
          <p className="text-slate-500 text-xs">{content.codeHelp}</p>

          {/* Validation result */}
          {validationMsg && (
            <div className={`mt-4 flex items-center gap-2 text-sm ${codeValid ? "text-emerald-400" : "text-red-400"}`} data-testid="validation-result">
              {codeValid ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {validationMsg}
            </div>
          )}

          {/* Activation form */}
          {showActivation && codeValid && (
            <div className="mt-6 pt-6 border-t border-[#1E3A5F]" data-testid="activation-form">
              <h3 className="text-base font-bold text-white mb-4">{content.activateTitle}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">{content.emailLabel}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={content.emailPlaceholder}
                    className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-4 py-2.5 text-white text-sm placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none"
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">{content.nameLabel}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={content.namePlaceholder}
                    className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-4 py-2.5 text-white text-sm placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none"
                    data-testid="name-input"
                  />
                </div>
                {activationError && (
                  <div className="text-red-400 text-xs flex items-center gap-1" data-testid="activation-error">
                    <AlertCircle className="w-3.5 h-3.5" /> {activationError}
                  </div>
                )}
                <button
                  onClick={handleActivate}
                  disabled={activating || !email.trim()}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white py-3 rounded font-bold text-sm transition-colors flex items-center justify-center gap-2"
                  data-testid="activate-btn"
                >
                  {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {content.activateBtn}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12" data-testid="features-grid">
          {content.features.map((f, i) => (
            <div key={i} className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-5 hover:border-[#1A6BCC]/40 transition-colors">
              <f.icon className="w-6 h-6 text-[#00C8E0] mb-3" />
              <h3 className="text-sm font-bold text-white mb-1">{f.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* QR Code reference */}
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-6 flex flex-col sm:flex-row items-center gap-6 max-w-xl mx-auto" data-testid="qr-reference">
          <div className="flex-1 text-center sm:text-left">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1 font-bold">{content.scanTitle}</div>
            <div className="text-[#00C8E0] font-mono text-sm">{content.scanUrl}</div>
          </div>
          <div className="w-px h-10 bg-[#1E3A5F] hidden sm:block" />
          <div className="flex-1 text-center sm:text-left">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1 font-bold">{content.calcTitle}</div>
            <div className="text-[#00C8E0] font-mono text-sm">{content.calcUrl}</div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-[#1E3A5F]/50">
          <p className="text-slate-600 text-xs">
            Brain Drain Series · Charles Muchina · CHARSTAN Intelligence Platform · charstan.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReaderHubLandingPage;
